// src/services/apaleo/offers.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { ServiceOfferModel } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData } from './config';

const part: ThisType<ApaleoClient> = {
async fetchOffers(
  propertyId: string | null,
  arrival: string,
  departure: string,
  adults = 1,
): Promise<unknown[]> {
  if (IS_DEV) return MockData.MOCK_OFFERS;
  if (!propertyId) {
    console.warn("fetchOffers called without propertyId — skipping");
    return [];
  }
  try {
    const url = `/booking/v1/offers?propertyId=${propertyId}&arrival=${arrival}&departure=${departure}&adults=${adults}`;

    const data = (await this._fetch(url)) as { offers?: unknown[] } | null;
    return data?.offers || [];
  } catch (error) {
    console.error("Fehler beim Laden der Buchungserstellung:", error);
    throw error;
  }
},

async createBooking(bookingPayload: Record<string, unknown>): Promise<unknown> {
  if (IS_DEV)
    return { id: "MOCK-BOOKING-ID", reservationIds: ["MOCK-RES-1"] };
  try {
    return await this._fetch("/booking/v1/bookings", {
      method: "POST",
      body: JSON.stringify(bookingPayload),
    });
  } catch (error) {
    console.error("Fehler beim Erstellen der Buchung", error);
    throw error;
  }
},

async fetchReservationServices(reservationId: string): Promise<ServiceOfferModel[]> {
  if (IS_DEV) return MockData.MOCK_SERVICES;
  try {
    const data = (await this._fetch(
      `/booking/v1/reservations/${reservationId}/service-offers?channelCode=Direct&onlyDefaultDates=false&includeUnavailable=false`,
    )) as
      | {
          serviceOffers?: ServiceOfferModel[];
          offers?: ServiceOfferModel[];
          services?: ServiceOfferModel[];
        }
      | null;
    return data?.serviceOffers || data?.offers || data?.services || [];
  } catch (error) {
    console.error(
      `Fehler beim Laden der Services für ${reservationId}:`,
      error,
    );
    throw error;
  }
},

async fetchServices(propertyId: string): Promise<ServiceOfferModel[]> {
  if (IS_DEV) return MockData.MOCK_SERVICES;
  try {
    const data = (await this._fetch(
      `/rateplan/v1/services?propertyId=${propertyId}&pageSize=100`,
    )) as { services?: ServiceOfferModel[] } | null;
    return data?.services || [];
  } catch (error) {
    console.error(
      `Fehler beim Laden der Services für Property ${propertyId}:`,
      error,
    );
    return [];
  }
},

async fetchReservationBookedServices(reservationId: string): Promise<ServiceOfferModel[]> {
  if (IS_DEV) return MockData.MOCK_SERVICES;
  try {
    const data = (await this._fetch(
      `/booking/v1/reservations/${reservationId}/services`,
    )) as { services?: ServiceOfferModel[] } | null;
    return data?.services || [];
  } catch (e) {
    console.warn(
      `Fehler beim Laden der gebuchten Services für ${reservationId}:`,
      e,
    );
    throw e;
  }
},

async bookReservationService(
  reservationId: string,
  servicePayload: Record<string, unknown>,
): Promise<unknown> {
  try {
    return await this._fetch(
      `/booking/v1/reservation-actions/${reservationId}/book-service`,
      {
        method: "PUT",
        body: JSON.stringify(servicePayload),
      },
    );
  } catch (error) {
    console.error(
      `Fehler beim Buchen des Services für ${reservationId}:`,
      error,
    );
    throw error;
  }
},

async deleteReservationService(
  reservationId: string,
  serviceId: string,
): Promise<unknown> {
  if (!reservationId) throw new Error("deleteReservationService: reservationId is required");
  if (!serviceId) throw new Error("deleteReservationService: serviceId is required");
  return await this._fetch(
    `/booking/v1/reservations/${encodeURIComponent(reservationId)}/services?serviceId=${encodeURIComponent(serviceId)}`,
    { method: "DELETE" },
  );
},

};

export default part;
